import { Data, noUndefinedFields, Response, waitAndRetry } from '@dimandzhi/graphql';
import { currentMethodName } from 'app/current-method-name';
import { GraphQL } from 'app/graphql';
import { environment as env } from 'environments/environment';
import _ from 'lodash';
import { first, map, switchMap, tap } from 'rxjs/operators';

type gqlType = GraphQL.ChannelStream;
const gqlType = GraphQL.ChannelStream;
const ModelName = gqlType.getModelName();
export class ChannelStreams extends gqlType.createServiceBaseConstructor() {
  queryStreamsOfChannel(channelID: number) {
    return this.queryItems({ args: `(ChannelRef: ${channelID})` });
  }

  updateChannelStreamOrder(streams: gqlType[]) {
    const methodName = currentMethodName();
    let values: string[] = [];
    streams.every((stream) => values.push(`{id: ${stream.id}, order: ${stream.order}}`));
    return this.http
      .post<Response>(
        `${env.graphql}/${methodName}`,
        `mutation {
          update {
            ${ModelName}(values:[${values.join(', ')}])
          }
        }`,
      )
      .pipe(
        first(),
        Data.fromResp,
        map((data) => {
          const result = data.upsertResp('update', ModelName);
          if (result === undefined) {
            throw new Error(`result is undefined`);
          }
          return result;
        }),
        map((rowsAffected) => {
          if (!_.isNumber(rowsAffected)) {
            throw new Error(`rowsAffected is not a number: ${typeof rowsAffected} ${rowsAffected}`);
          }
          return rowsAffected;
        }),
        switchMap((rowsAffected) =>
          this.http
            .post<Response>(
              `${env.graphql}/${methodName}-queryAffectedRecords`,
              `query {
                ${ModelName} {
                  filter(id: [${streams.map((stream) => stream.id).join(',')}]) {
                    data {
                      ${gqlType.fields({ withSubFields: true })}
                    }
                  }
                }
              }`,
            )
            .pipe(
              waitAndRetry,
              Data.fromResp,
              map((data) => gqlType.fromData(data, `filter.data`)),
              noUndefinedFields(),
              tap({
                next: (changedStreams) => {
                  this.mutateItems((streams) => {
                    for (const stream of changedStreams) {
                      let i = streams.findIndex((s) => s.id === stream.id);
                      streams[i] = stream;
                    }
                  });
                },
              }),
              map(() => rowsAffected),
            ),
        ),
      );
  }

  replaceChannelStream(oldID: number, newID: number) {
    const methodName = currentMethodName();
    return this.http
      .post<Response>(
        `${env.graphql}/${methodName}`,
        `mutation {
          replaceChannelStreams(old: ${oldID}, new: ${newID})
        }`,
      )
      .pipe(
        first(),
        Data.fromResp,
        switchMap((data) => {
          let IDs = this.getSnapshot()
            .filter((stream) => stream.replacedWith !== null && [oldID, newID].includes(stream.replacedWith))
            .map((stream) => stream.id);
          IDs.push(oldID, newID);
          return this.http
            .post<Response>(
              `${env.graphql}/${methodName}-queryAffectedRecords`,
              `query {
                ${ModelName} {
                  filter(id: ${JSON.stringify(IDs)}) {
                    data {
                      ${gqlType.fields({ withSubFields: true })}
                    }
                  }
                }
              }`,
            )
            .pipe(
              waitAndRetry,
              Data.fromResp,
              map((data) => gqlType.fromData(data, 'filter.data')),
              noUndefinedFields(),
              map((changedStreams) => {
                this.mutateItems((streams) => {
                  let i: number;
                  for (const stream of changedStreams) {
                    i = _.findIndex(streams, (s) => s.id === stream.id);
                    streams[i] = stream;
                  }
                });
                return changedStreams;
              }),
            );
        }),
      );
  }
}
